import React from "react";
import { useTheme } from "@mui/material/styles";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import PublicPageLayout from "../public-site/PublicPageLayout";
import { getPublicSiteSx } from "../public-site/publicSiteStyles";

export default function AboutUsPage() {
  const theme = useTheme();
  const sx = React.useMemo(() => getPublicSiteSx(theme), [theme]);

  return (
    <PublicPageLayout
      eyebrow="About Ouro"
      title="Built by a player, now looking for the first players who believe."
      description="Ouro started from a simple frustration: too many competitive matches feel disposable because the systems around them make it easy for trust, accountability, and player quality to break down. The next step is building the founding community that can prove a better queue is possible."
      mapBackground
    >
      <Box sx={sx.section}>
        <Box sx={sx.sectionHeader}>
          <Typography sx={sx.sectionTitle}>Built from the queue problem.</Typography>
        </Box>
        <Box sx={{ ...sx.cardGrid, gridTemplateColumns: { xs: "1fr", lg: "1.1fr 0.9fr" } }}>
          <Box sx={sx.card}>
            <Typography sx={sx.cardTitle}>Why Ouro exists</Typography>
            <Typography sx={{ ...sx.cardBody, mb: 2 }}>
              Ouro was created for Counter-Strike players who care deeply about
              the quality of the people they queue with, not only the end score
              of the match. The platform is shaped around the idea that better
              games come from better standards: stronger onboarding, clearer
              accountability, and systems that reward players who show up well.
            </Typography>
            <Typography sx={sx.cardBody}>
              The hard part now is not spinning up servers; it is gathering
              enough committed players in each region for queue windows to feel
              healthy. Instead of pretending the network already exists, Ouro is
              building it openly with the players who want it most.
            </Typography>
          </Box>

          <Box sx={sx.accentPanel}>
            <Typography sx={sx.cardTitle}>The platform promise</Typography>
            <Box component="ul" sx={sx.bulletList}>
              <li>
                <Typography component="span" sx={sx.listItemText}>
                  Cleaner matches with fewer throwaway-account dynamics.
                </Typography>
              </li>
              <li>
                <Typography component="span" sx={sx.listItemText}>
                  A stronger trust layer around who gets to participate.
                </Typography>
              </li>
              <li>
                <Typography component="span" sx={sx.listItemText}>
                  Memberships that unlock real queue control instead of fluff.
                </Typography>
              </li>
              <li>
                <Typography component="span" sx={sx.listItemText}>
                  Regional queue windows that expand as the player base grows.
                </Typography>
              </li>
              <li>
                <Typography component="span" sx={sx.listItemText}>
                  A community model that lets early players influence what gets built.
                </Typography>
              </li>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={sx.section}>
        <Box sx={sx.sectionHeader}>
          <Typography sx={sx.sectionTitle}>About the team</Typography>
        </Box>
        <Box sx={sx.card}>
          <Typography sx={{ ...sx.cardBody, mb: 2 }}>
            Ouro is built by a small team of engineers and competitive
            players. The platform comes out of years spent both building
            software and playing competitive games, including thousands of
            hours in Counter-Strike.
          </Typography>
          <Typography sx={sx.cardBody}>
            That background matters because the product was not born from a
            generic startup pitch. It came from firsthand experience with how
            often competitive queues fail players who actually care about match
            integrity, teammate quality, and the feeling that the game was worth
            their time. Early players are not just signups here; they are the
            people helping decide whether this better version of matchmaking can
            exist at real scale.
          </Typography>
        </Box>
      </Box>

      <Box sx={sx.section}>
        <Box sx={sx.sectionHeader}>
          <Typography sx={sx.sectionTitle}>A domain with a story.</Typography>
        </Box>
        <Box sx={sx.card}>
          <Typography sx={{ ...sx.cardBody, mb: 2 }}>
            In August 2024, when Ouro was still just an idea and a lot of
            unanswered questions,{" "}
            <Box component="span" sx={{ fontWeight: 600 }}>
              Björn Heiðar Þórarinsson
            </Box>
            {" "}— known on Discord as{" "}
            <Box component="span" sx={{ fontStyle: "italic" }}>
              bjoerntastic
            </Box>
            {" "}— took the time to walk through how CS2 custom servers actually
            work on Linux, how to manage them, and what is realistically possible
            to build on top of them. A Counter-Strike player since 2000, Björn
            brought over two decades of experience to that conversation and
            turned a vague ambition into a concrete plan.
          </Typography>
          <Typography sx={{ ...sx.cardBody, mb: 2 }}>
            Björn is from Iceland, and when it came time to register a
            domain, ouro.is felt right. The{" "}
            <Box component="span" sx={{ fontWeight: 600 }}>.is</Box> top-level
            domain is Iceland's, and choosing it was a small, deliberate nod to
            the person who helped make Ouro real before it had a single line of
            code running on a server.
          </Typography>
          <Typography sx={sx.cardBody}>
            We are just like that — caring about the little details and
            appreciating the people we meet along the journey.
          </Typography>
        </Box>
      </Box>
    </PublicPageLayout>
  );
}
